import { useEffect, useMemo, useRef, useState } from 'react';
import type { AccommodationListingDTO, Filters } from '@shared/types/Accommodations.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { SortOption } from '@shared/constants/accommodations';

const PAGE_SIZE = 30;

/**
 * Maximum number of listing changes (adds + removes) to treat as a minor
 * update rather than a full list refresh. When a couple adds or removes a
 * hotel from their page the consolidated listing set typically changes by
 * one or two items (the couple-pick replaces the regional entry, or vice
 * versa). A new search or filter change replaces most of the list.
 */
const MINOR_CHANGE_THRESHOLD = 3;

type UsePaginationArgs = Readonly<{
  listings: ReadonlyArray<AccommodationListingDTO>;
  filters: Filters;
  sortOption?: SortOption;
  /** Live prices are still streaming in. While true the reset heuristic below is suspended: a batch
   *  landing can push listings in or out of an active price filter, which reads as a "substantial
   *  change" and would throw a user who has clicked "Show more" back to the top of page one — from a
   *  background response they never initiated. */
  isRefreshingListingPrices?: boolean;
}>;

/**
 * Derive a stable identifier for a listing so we can tell whether the
 * underlying hotel changed, even when the `id` field differs between the
 * regional entry and the couple-pick entry for the same property.
 */
const stableId = (listing: AccommodationListingDTO) => listing.joyPlaceId ?? listing.id;

export const usePagination = (args: UsePaginationArgs) => {
  const { listings, filters, sortOption, isRefreshingListingPrices } = args;

  const listingsContainerRef = useRef<HTMLDivElement>(null);
  const stableIdsRef = useRef<Set<string>>();
  if (!stableIdsRef.current) {
    stableIdsRef.current = new Set(listings.map(stableId));
  }
  const sortOptionRef = useRef(sortOption);
  // Filters live in the URL, so a change here is always the user's — never a price batch landing.
  const filtersKey = JSON.stringify(filters);
  const filtersKeyRef = useRef(filtersKey);

  const [page, setPage] = useState(0);

  const scrollToTop = () => {
    if (listingsContainerRef.current) {
      listingsContainerRef.current.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    }
    setPage(0);
  };

  useEffect(() => {
    // Sort option changed — always reset to the top so the user sees the new
    // top-ranked results (e.g. switching from Distance to Price). Checked before the batching guard
    // below: a sort the user picks mid-batch must take effect now, not when the batches settle.
    if (sortOption !== sortOptionRef.current) {
      sortOptionRef.current = sortOption;
      filtersKeyRef.current = filtersKey;
      stableIdsRef.current = new Set(listings.map(stableId));
      scrollToTop();
      return;
    }

    const filtersChanged = filtersKey !== filtersKeyRef.current;
    filtersKeyRef.current = filtersKey;

    // A price batch landing is not a user action; never reset scroll or page for one. The set is
    // re-baselined on settle by the next run of this effect. A filter change is the user's, so it
    // falls through to the normal reset logic even mid-batch.
    if (isRefreshingListingPrices && !filtersChanged) {
      stableIdsRef.current = new Set(listings.map(stableId));
      return;
    }

    const nextStableIds = new Set(listings.map(stableId));
    // Always defined: the ref is populated before the first render completes.
    const prevStableIds = stableIdsRef.current!;

    // Count how many hotels were added or removed compared to the previous set.
    let changes = 0;
    for (const id of nextStableIds) {
      if (!prevStableIds.has(id)) changes++;
    }
    for (const id of prevStableIds) {
      if (!nextStableIds.has(id)) changes++;
    }

    stableIdsRef.current = nextStableIds;

    if (changes === 0) {
      // Same set of hotels, just reordered (e.g. couple-pick moved to front) — no scroll.
      return;
    }

    if (changes <= MINOR_CHANGE_THRESHOLD) {
      // Small change: a hotel was added/removed from the page. Keep the scroll
      // position so the user doesn't lose their place.
      return;
    }

    // Substantial change (new search, filter, etc.) — reset to the top.
    scrollToTop();
  }, [listings, filters.pickedByCouple, filtersKey, sortOption, isRefreshingListingPrices]);

  const { hasMoreListingsToShow, paginatedListings } = useMemo(() => {
    const numberOfListingsToShow = (page + 1) * PAGE_SIZE;
    return {
      hasMoreListingsToShow: numberOfListingsToShow < listings.length,
      paginatedListings: listings.slice(0, numberOfListingsToShow),
    };
  }, [page, listings]);

  const showNextPageOfListings = useEventCallback(() => {
    setPage(prevPage => prevPage + 1);
  });

  return {
    paginatedListings,
    hasMoreListingsToShow,
    showNextPageOfListings,
    listingsContainerRef,
  };
};
